import React, { type FC, useRef, useState } from 'react';
import { Typography, Grid, IconButton, Link } from '@mui/material';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import sx from 'mui-sx';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import { VolumeOffRounded } from '@mui/icons-material';

import { type ILingoLiftSummarySpokenDialogWord } from '../../../../model/types';
import {
  LingoLiftSummaryWordEvaluation,
  LingoLiftSummaryWordStatus,
} from '../../../../model/enums';
import Div from '../../../../../../shared/ui/Div';

import styles from './styles';

interface ISpeakRow {
  words?: ILingoLiftSummarySpokenDialogWord[];
  score: string;
  attempts: string;
  audioUrl: string;
  transcript: string;
  grade: number;
  pronunciationScoreLabel: string;
  attemptsLabel: string;
}

const SpeakRow: FC<ISpeakRow> = props => {
  const {
    words,
    transcript,
    score,
    grade,
    attempts,
    audioUrl,
    pronunciationScoreLabel,
    attemptsLabel,
  } = props;
  const [isAudioPlayer, setIsAudioPlayer] = useState(false);
  const audioRef = useRef<{ current: HTMLAudioElement }>();

  const onStartAudio = () => {
    setIsAudioPlayer(!isAudioPlayer);
    // @ts-expect-error - HTMLAudioElement type is not properly inferred from useRef
    audioRef.current.play();
  };

  const onStopAudio = () => {
    setIsAudioPlayer(!isAudioPlayer);
    // @ts-expect-error - HTMLAudioElement type is not properly inferred from useRef
    audioRef.current.pause();
  };

  return (
    <Div sx={styles.root}>
      <audio ref={audioRef as unknown as { current: HTMLAudioElement }} id="myAudio">
        <source src={audioUrl} type="audio/mpeg" />
      </audio>

      <Grid container spacing={3} justifyContent="space-between">
        <Grid item xs={9}>
          <Div sx={styles.sentenceWrapper}>
            {(!words || !Array.isArray(words) || words.length === 0) && transcript && (
              <Typography component="span" sx={sx(styles.title)}>
                {transcript}
              </Typography>
            )}

            {words?.map((item, index) => {
              const { label, status, evaluation, learned } = item;

              if (
                evaluation === LingoLiftSummaryWordEvaluation.Pause ||
                (evaluation === LingoLiftSummaryWordEvaluation.Match && label.includes('#SIL#'))
              ) {
                return (
                  <Typography key={index} component="span" sx={sx(styles.pause)}>
                    <Div sx={styles.pauseWrapper}>
                      <Div sx={styles.pauseContainer}>
                        <Div sx={styles.pauseIconContainer}>
                          <LocalParkingIcon sx={styles.pauseIcon} />
                        </Div>
                      </Div>
                    </Div>
                  </Typography>
                );
              }

              return (
                <Typography
                  key={index}
                  component="span"
                  variant="h5"
                  sx={sx(
                    {
                      ...styles.title,
                    },
                    {
                      sx: styles.clear,
                      condition:
                        evaluation === LingoLiftSummaryWordEvaluation.Match && status === 'GOOD',
                    },
                    {
                      sx: styles.satisfactory,
                      condition: evaluation === LingoLiftSummaryWordEvaluation.Substitution,
                    },
                    {
                      sx: styles.poor,
                      condition:
                        evaluation === LingoLiftSummaryWordEvaluation.Deletion ||
                        (evaluation === LingoLiftSummaryWordEvaluation.Match && status === 'BAD'),
                    },
                    {
                      sx: styles.learned,
                      condition: learned === true,
                    },
                  )}
                >
                  {evaluation === LingoLiftSummaryWordEvaluation.Insertion && (
                    <Div sx={styles.addWrapper}>
                      <Div sx={styles.addContainer}>
                        <Div sx={styles.addIconContainer}>
                          <AddBoxRoundedIcon sx={styles.addIcon} />
                        </Div>
                      </Div>
                    </Div>
                  )}
                  {evaluation !== LingoLiftSummaryWordEvaluation.Insertion && label}
                </Typography>
              );
            })}
          </Div>
        </Grid>
        <Grid item xs={1.5}>
          <Typography variant="body2" sx={styles.description}>
            {pronunciationScoreLabel}
          </Typography>
          <Div sx={styles.wrapper}>
            {(
              <Typography variant="h5" sx={styles.level}>
                {grade || 'N/A'}
              </Typography>
            )}
            <Div sx={styles.wrapper}>
              <Typography variant="h5" sx={styles.score}>
                {score}
              </Typography>
            </Div>
          </Div>
        </Grid>
        <Grid item xs={1.5}>
          <Div sx={styles.wrapper}>
            {/* <Div sx={ styles.attemptsWrapper }>
              <Typography variant="body2" sx={ styles.description }>
                {attemptsLabel}
              </Typography>
              <Typography variant="h5" sx={ styles.title }>
                { attempts }
              </Typography>
            </Div> */}
            <Div>
              {!isAudioPlayer && (
                <IconButton onClick={onStartAudio} sx={styles.iconButton}>
                  <VolumeUpOutlinedIcon />
                </IconButton>
              )}

              {isAudioPlayer && (
                <IconButton onClick={onStopAudio} sx={styles.iconButton}>
                  <VolumeOffRounded />
                </IconButton>
              )}
              <IconButton sx={styles.iconButton}>
                <Link target="_blank" download href={audioUrl}>
                  <FileDownloadOutlinedIcon />
                </Link>
              </IconButton>
            </Div>
          </Div>
        </Grid>
      </Grid>
    </Div>
  );
};

export default SpeakRow;
